import { Plane3D, PointVector3D, Mesh, Vertex, Face, Circle3D } from "./geometry";
import Mat4 from "./mat4";

export class Shape {
    basePlane: Plane3D;
    origin: PointVector3D;
    normal: PointVector3D;
    mesh: Mesh;
    axis: PointVector3D;
    wireframeScale: number = 1;
    overlayScale: number = 1;
    fov: number = 1.6;
    _projectionMatrix!: Mat4;
    _modelViewMatrix!: Mat4;
    relativeTransformation: {
        movement: PointVector3D,
        rotation: PointVector3D,
        scale: PointVector3D,
    } = {
        movement: new PointVector3D(0, 0, 0),
        rotation: new PointVector3D(0, 0, 0),
        scale: new PointVector3D(1, 1, 1),
    }
    relativeTransformationMatrix?: Mat4;

    constructor(basePlane: Plane3D, mesh: Mesh, axis: PointVector3D) {
        this.basePlane = basePlane;
        this.origin = this.basePlane.origin;
        this.normal = this.basePlane.normal;
        this.mesh = mesh;
        this.axis = axis;
        this.generateProjectionMatrix();
        this.generateModelViewMatrix();
    }

    generateProjectionMatrix(aspect = 1, fov = this.fov, near = 0.1, far = 5000) {
        this._projectionMatrix = Mat4.perspective(fov, aspect, near, far);
    }

    generateModelViewMatrix(x: number = 0, y: number = 0, z: number = 0) {
        this._modelViewMatrix = Mat4.translation(x, y, z);
    }

    move(x: number, y: number, z: number) {
        this._modelViewMatrix = this._modelViewMatrix.translate(x, y, z);
    }

    rotate(x: number, y: number, z: number) {
        this._modelViewMatrix = this._modelViewMatrix.rotate(x, y, z);
    }

    scale(x: number, y: number, z: number) {
        this._modelViewMatrix = this._modelViewMatrix.scale(x, y, z);
    }

    scaleUniform(scale: number) {
        this._modelViewMatrix = this._modelViewMatrix.scale(scale, scale, scale);
    }

    relativeUniformScale(scale: number) {
        if (!this.relativeTransformationMatrix) {
            this.relativeTransformationMatrix =  Mat4.translation(0, 0, 0);
        }
        this.relativeTransformationMatrix = this.relativeTransformationMatrix.scale(scale, scale, scale);
    }

    relativeMove(x: number, y: number, z: number) {
        if (!this.relativeTransformationMatrix) {
            this.relativeTransformationMatrix =  Mat4.translation(0, 0, 0);
        }
        this.relativeTransformationMatrix = this.relativeTransformationMatrix.translate(x, y, z);
    }

    relativeRotate(x: number, y: number, z: number) {
        if (!this.relativeTransformationMatrix) {
            this.relativeTransformationMatrix =  Mat4.translation(0, 0, 0);
        }
        this.relativeTransformationMatrix = this.relativeTransformationMatrix.rotate(x, y, z);
    }

    relativeClear() {
        this.relativeTransformationMatrix = undefined;
    }

    get modelViewMatrix(): Mat4 {
        if (this.relativeTransformationMatrix) return this._modelViewMatrix.multiply(this.relativeTransformationMatrix);
        else return this._modelViewMatrix;
    }

    get projectionMatrix(): Mat4 {
        return this._projectionMatrix;
    }
}

export class Surface extends Shape {
    constructor(basePlane: Plane3D, width: number, length: number, rotation = 0) {
        const origin = basePlane.origin;
        const normal = basePlane.normal;
        basePlane.rotate(normal, rotation, origin);

        const points = [];
        const vertices = [];
        points.push( basePlane.xyToXyz( new PointVector3D(- width / 2, - length / 2) ) );
        points.push( basePlane.xyToXyz( new PointVector3D(  width / 2, - length / 2) ) );
        points.push( basePlane.xyToXyz( new PointVector3D(  width / 2,   length / 2) ) );
        points.push( basePlane.xyToXyz( new PointVector3D(- width / 2,   length / 2) ) );
        for (let v = 0; v < points.length; v++) {
            vertices[v] = new Vertex(points[v]);
        }
        const faces = [new Face( [ 0, 1, 2, 3 ] )];
        const mesh = new Mesh(vertices, faces);
        let axis = normal.copy();

        super(basePlane, mesh, axis)
    }
}

export class Box extends Shape {
    constructor(basePlane: Plane3D, width: number, length: number, height: number, rotation = 0) {
        const origin = basePlane.origin;
        const normal = basePlane.normal;
        basePlane.rotate(normal, rotation, origin);

        const halfHight = normal.copy().multiply(height/2);
        const baseCoords = [];
        const points = [];
        const vertices = [];
        const faces = [];
        baseCoords.push( basePlane.xyToXyz( new PointVector3D(- width / 2, - length / 2) ) );
        baseCoords.push( basePlane.xyToXyz( new PointVector3D(  width / 2, - length / 2) ) );
        baseCoords.push( basePlane.xyToXyz( new PointVector3D(  width / 2,   length / 2) ) );
        baseCoords.push( basePlane.xyToXyz( new PointVector3D(- width / 2,   length / 2) ) );
        for (let i = 0; i < baseCoords.length; i ++) {
            points.push(baseCoords[i].copy().add(halfHight));
        }
        halfHight.reverse();
        const bottomCenter = origin.copy();
        bottomCenter.add(halfHight);
        for (let i = 0; i < baseCoords.length; i ++) {
            points.push(baseCoords[i].copy().add(halfHight));
        }
        for (let v = 0; v < points.length; v++) {
            vertices[v] = new Vertex(points[v]);
        }
        faces.push(new Face( [ 0, 1, 2, 3 ] ))
        faces.push(new Face( [ 0, 1, 5, 4 ] ))
        faces.push(new Face( [ 1, 2, 6, 5 ] ))
        faces.push(new Face( [ 2, 3, 7, 6 ] ))
        faces.push(new Face( [ 3, 0, 4, 7 ] ))
        faces.push(new Face( [ 4, 5, 6, 7 ] ))
        const mesh = new Mesh(vertices, faces);
        let axis = halfHight.copy();
        axis = axis.multiply(-2);

        super(basePlane, mesh, axis)
    }
}

export class Shape_02 extends Shape {
    constructor(basePlane: Plane3D, bigRadius: number, smallRadius: number, height: number, sides: number, rotation = 0) {
        const origin = basePlane.origin;
        const normal = basePlane.normal;
        basePlane.rotate(normal, rotation, origin);

        const bigCircle = new Circle3D(basePlane, bigRadius);
        const smallCircle = new Circle3D(basePlane, smallRadius);
        const points = [];
        const vertices = [];
        const faces = [];

        const topVertex = origin.copy();
        const vector = normal.copy();
        vector.multiply(height);
        points.push(topVertex.add(vector));
        const bigCircleVertices = bigCircle.subdivide(sides);
        const smallCircleVertices = smallCircle.subdivide(sides);
        for (let s = 0; s < sides; s++) {
            points.push(bigCircleVertices[s])
            smallCircleVertices[s].rotate(normal, Math.PI / sides, origin);
            points.push(smallCircleVertices[s])
        }
        const bottomVertex = origin.copy();
        vector.reverse();
        points.push(bottomVertex.add(vector));
        for (let v = 0; v < points.length; v++) {
            vertices[v] = new Vertex(points[v]);
        }
        for (let s = 0; s < sides*2; s++) {
            let v1 = s + 1;
            let v2 = s == sides*2-1 ? 1 : s + 2;
            faces.push(new Face( [ 0, v1, v2 ] ))
            faces.push(new Face( [ vertices.length - 1, v1, v2 ] ))
        }
        const mesh = new Mesh(vertices, faces);
        let axis = topVertex.copy();
        axis = axis.subtract(bottomVertex);

        super(basePlane, mesh, axis);
    }
}

export class Shape_03 extends Shape {
    constructor(basePlane: Plane3D, bigRadius: number, smallRadius: number, height1: number, height2: number, sides: number, rotation = 0) {
        const origin = basePlane.origin;
        const normal = basePlane.normal;
        basePlane.rotate(normal, rotation, origin);

        const bigCircle = new Circle3D(basePlane, bigRadius);
        const smallCircle = new Circle3D(basePlane, smallRadius);
        const points = [];
        const vertices = [];
        const faces = [];

        const topVertex = origin.copy();
        const vector1 = normal.copy();
        vector1.multiply(height1);
        const vector2 = normal.copy();
        vector2.multiply(height2);
        points.push(topVertex.add(vector2));
        const atp = origin.copy();
        atp.add(vector1);
        const bigCircleVertices = bigCircle.subdivide(sides);
        const smallCircleVertices = smallCircle.subdivide(sides);
        for (let s = 0; s < sides; s++) {
            const tp = bigCircleVertices[s].copy();
            points.push(tp.add(vector1));
        }
        for (let s = 0; s < sides; s++) {
            smallCircleVertices[s].rotate(normal, Math.PI / sides, origin);
            points.push(smallCircleVertices[s])
        }
        vector1.reverse();
        const abp = origin.copy();
        abp.add(vector1);
        for (let s = 0; s < sides; s++) {
            const tp = bigCircleVertices[s].copy();
            points.push(tp.add(vector1));
        }
        const bottomVertex = origin.copy();
        vector2.reverse();
        points.push(bottomVertex.add(vector2));
        for (let v = 0; v < points.length; v++) {
            vertices[v] = new Vertex(points[v]);
        }
        for (let s = 0; s < sides; s++) {
            const vt = s + 1;
            const vb = s + sides*2 + 1;
            const v1 = s == 0 ? s + sides*2 : s + sides;
            const v2 = s + sides + 1;
            faces.push(new Face( [ 0, vt, v1 ] ))
            faces.push(new Face( [ 0, vt, v2 ] ))
            faces.push(new Face( [ v1, v2, vt ] ))
            faces.push(new Face( [ vertices.length - 1, vb, v1 ] ))
            faces.push(new Face( [ vertices.length - 1, vb, v2 ] ))
            faces.push(new Face( [ v1, v2, vb ] ))
        }
        const mesh = new Mesh(vertices, faces);
        const axis = atp.subtract(abp);

        super(basePlane, mesh, axis)
    }
}

export class Shape_04 extends Shape {
    constructor(basePlane: Plane3D, bigRadius: number, height1: number, height2: number, sides: number, rotation = 0) {
        const origin = basePlane.origin;
        const normal = basePlane.normal;
        basePlane.rotate(normal, rotation, origin);

        const bigCircle = new Circle3D(basePlane, bigRadius);
        const points = [];
        const vertices = [];
        const faces = [];

        const topVertex = origin.copy();
        const vector1 = normal.copy();
        vector1.multiply(height1);
        const vector2 = normal.copy();
        vector2.multiply(height2);
        points.push(topVertex.add(vector2));
        const atp = origin.copy();
        atp.add(vector1);
        const bigCircleVertices = bigCircle.subdivide(sides);
        for (let s = 0; s < sides; s++) {
            const tp = bigCircleVertices[s].copy();
            points.push(tp.add(vector1));
        }
        vector1.reverse();
        const abp = origin.copy();
        abp.add(vector1);
        for (let s = 0; s < sides; s++) {
            const tp = bigCircleVertices[s].copy();
            tp.rotate(normal, Math.PI / sides, origin);
            points.push(tp.add(vector1));
        }
        const bottomVertex = origin.copy();
        vector2.reverse();
        points.push(bottomVertex.add(vector2));
        for (let v = 0; v < points.length; v++) {
            vertices[v] = new Vertex(points[v]);
        }
        for (let s = 0; s < sides; s++) {
            const v1 = s + 1;
            const v2 = s == 0 ? sides*2 : s + sides;
            const v3 = s == 0 ? sides : s;
            const v4 = s + sides + 1;
            faces.push(new Face( [ 0, v1, v2 ] ))
            faces.push(new Face( [ 0, v1, v4 ] ))
            faces.push(new Face( [ vertices.length - 1, v2, v1 ] ))
            faces.push(new Face( [ vertices.length - 1, v2, v3 ] ))
        }
        const mesh = new Mesh(vertices, faces);
        const axis = atp.subtract(abp);

        super(basePlane, mesh, axis)
    }
}

export class Shape_05 extends Shape {
    constructor(basePlane: Plane3D, bigRadius: number, smallRadius: number, height1: number, height2: number, height3: number, sides: number, rotation = 0) {
        const origin = basePlane.origin;
        const normal = basePlane.normal;
        basePlane.rotate(normal, rotation, origin);

        const bigCircle = new Circle3D(basePlane, bigRadius);
        const smallCircle = new Circle3D(basePlane, smallRadius);
        const points = [];
        const vertices = [];
        const faces = [];
    
        const topVertex = origin.copy();
        const vector1 = normal.copy();
        vector1.multiply(height1);
        const vector2 = normal.copy();
        vector2.multiply(height2);
        const vector3 = normal.copy();
        vector3.multiply(height3);
        points.push(topVertex.add(vector3));
        const atp = origin.copy();
        atp.add(vector1);
        const bigCircleVertices = bigCircle.subdivide(sides);
        const smallCircleVertices = smallCircle.subdivide(sides);
        for (let s = 0; s < sides; s++) {
            const tp = bigCircleVertices[s];
            smallCircleVertices[s].rotate(normal, Math.PI / sides, origin);
            points.push(tp.add(vector1));
            points.push(smallCircleVertices[s].add(vector2));
        }
        const bottomVertex = origin.copy();
        points.push(bottomVertex);
        vector1.multiply(0.5).reverse();
        for (let v = 0; v < points.length; v++) {
            points[v].add(vector1);
            vertices[v] = new Vertex(points[v]);
        }
        for (let s = 0; s < sides*2; s++) {
            const v1 = s + 1;
            const v2 = s == sides*2-1 ? 1 : s + 2;
            faces.push(new Face( [ 0, v1, v2 ] ))
            faces.push(new Face( [ vertices.length - 1, v1, v2 ] ))
        }
        const mesh = new Mesh(vertices, faces);
        const axis = atp.subtract(bottomVertex);

        super(basePlane, mesh, axis)
    }
}